import { useState, useEffect, useMemo, useCallback } from 'react';
import { collection, onSnapshot, query, orderBy, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db, addAuditLog } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import PageHeader from '../../components/PageHeader';
import ToastNotification from '../../components/ToastNotification';
import styles from './ReportsPage.module.css';

const TABS = ['Branch Performance', 'System Summary', 'Weekly Profits'];

function RatingBadge({ rating }) {
  const cls = rating === 'High' ? styles.ratingHigh
    : rating === 'Medium' ? styles.ratingMedium
    : styles.ratingLow;
  return <span className={`${styles.badge} ${cls}`}>{rating}</span>;
}

function ratingFromOrders(count) {
  if (count >= 10) return 'High';
  if (count >= 5)  return 'Medium';
  return 'Low';
}

function php(n) {
  return `₱${(n || 0).toLocaleString()}`;
}

function formatWeekLabel(mondayStr) {
  if (!mondayStr) return '—';
  const start = new Date(mondayStr + 'T00:00:00');
  const end   = new Date(mondayStr + 'T00:00:00');
  end.setDate(end.getDate() + 6);
  const fmt = d => d.toLocaleDateString('en-PH', { month: 'short', day: 'numeric' });
  return `${fmt(start)} – ${fmt(end)}, ${end.getFullYear()}`;
}

function BranchPerfTab({ branchPerf }) {
  if (branchPerf.length === 0) {
    return <p className={styles.perfNote}>No order data yet.</p>;
  }
  return (
    <>
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Rank</th>
              <th>Branch</th>
              <th>Orders</th>
              <th>Total Revenue</th>
              <th>Avg Order Value</th>
              <th>Rating</th>
            </tr>
          </thead>
          <tbody>
            {branchPerf.map((b, i) => (
              <tr key={b.branch} className={i % 2 === 0 ? styles.rowEven : styles.rowOdd}>
                <td className={styles.rank}>{i + 1}</td>
                <td className={styles.branchName}>{b.branch}</td>
                <td>{b.orders}</td>
                <td className={styles.amount}>₱{b.total.toLocaleString()}</td>
                <td>₱{b.avgOrder.toLocaleString()}</td>
                <td><RatingBadge rating={b.rating} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className={styles.perfNote}>Branches with Low rating may need supply chain support.</p>
    </>
  );
}

function SystemSummaryTab({ stats }) {
  return (
    <div className={styles.statsGrid}>
      {stats.map(stat => (
        <div key={stat.label} className={styles.statCard}>
          <span className={styles.statValue}>{stat.value}</span>
          <span className={styles.statLabel}>{stat.label}</span>
        </div>
      ))}
    </div>
  );
}

function WeeklyProfitsTab({ records, onEdit }) {
  const [weekFilter, setWeekFilter] = useState('All');

  const uniqueWeeks = useMemo(() => {
    const seen = new Set();
    const weeks = [];
    for (const r of records) {
      if (!seen.has(r.weekStart)) {
        seen.add(r.weekStart);
        weeks.push({ weekStart: r.weekStart, label: r.weekLabel || formatWeekLabel(r.weekStart) });
      }
    }
    return weeks;
  }, [records]);

  const filtered = weekFilter === 'All'
    ? records
    : records.filter(r => r.weekStart === weekFilter);

  const weeklySummary = useMemo(() => {
    const map = {};
    for (const r of records) {
      const key = r.weekStart;
      if (!map[key]) map[key] = { weekStart: key, weekLabel: r.weekLabel || formatWeekLabel(key), revenue: 0, expense: 0, net: 0, branches: 0 };
      map[key].revenue  += r.weeklyRevenue || 0;
      map[key].expense  += r.supplyExpense  || 0;
      map[key].net      += r.netProfit      || 0;
      map[key].branches += 1;
    }
    return Object.values(map).sort((a, b) => b.weekStart.localeCompare(a.weekStart));
  }, [records]);

  const totals = useMemo(() => filtered.reduce(
    (acc, r) => ({
      revenue: acc.revenue + (r.weeklyRevenue || 0),
      expense: acc.expense + (r.supplyExpense  || 0),
      net:     acc.net     + (r.netProfit      || 0),
    }),
    { revenue: 0, expense: 0, net: 0 },
  ), [filtered]);

  return (
    <div className={styles.wpSection}>
      {/* Totals */}
      <div className={styles.wpStatsRow}>
        <div className={styles.wpStatCard}>
          <span className={styles.statValue}>{php(totals.expense)}</span>
          <span className={styles.statLabel}>Weekly Order Expense</span>
        </div>
        <div className={styles.wpStatCard}>
          <span className={styles.statValue}>{php(totals.revenue)}</span>
          <span className={styles.statLabel}>Weekly Revenue</span>
        </div>
        <div className={styles.wpStatCard}>
          <span className={`${styles.statValue} ${totals.net >= 0 ? styles.positive : styles.negative}`}>
            {php(totals.net)}
          </span>
          <span className={styles.statLabel}>Weekly Net Profit</span>
        </div>
      </div>

      {/* Weekly summary */}
      <div className={styles.sectionTitle}>Weekly Summary (All Branches)</div>
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Week</th>
              <th>Branches Reported</th>
              <th>Weekly Order Expense</th>
              <th>Weekly Revenue</th>
              <th>Weekly Net Profit</th>
            </tr>
          </thead>
          <tbody>
            {weeklySummary.length === 0 ? (
              <tr><td colSpan={5} className={styles.emptyCell}>No weekly profit records yet.</td></tr>
            ) : weeklySummary.map((w, i) => (
              <tr key={w.weekStart} className={i % 2 === 0 ? styles.rowEven : styles.rowOdd}>
                <td className={styles.weekCell}><i className="ti ti-calendar" /> {w.weekLabel}</td>
                <td style={{ textAlign: 'center' }}>{w.branches}</td>
                <td className={styles.amount}>{php(w.expense)}</td>
                <td className={styles.amount}>{php(w.revenue)}</td>
                <td className={`${styles.amount} ${w.net >= 0 ? styles.positive : styles.negative}`}>{php(w.net)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Branch breakdown */}
      <div className={styles.wpFilterRow}>
        <span className={styles.sectionTitle}>Branch Breakdown</span>
        <div className={styles.filterWrap}>
          <label className={styles.filterLabel}>Filter by Week:</label>
          <select
            className={styles.filterSelect}
            value={weekFilter}
            onChange={e => setWeekFilter(e.target.value)}
          >
            <option value="All">All Weeks</option>
            {uniqueWeeks.map(w => (
              <option key={w.weekStart} value={w.weekStart}>{w.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Week</th>
              <th>Branch</th>
              <th>Weekly Revenue</th>
              <th>Supply Order Expense</th>
              <th>Net Profit</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={6} className={styles.emptyCell}>No records match the selected week.</td></tr>
            ) : filtered.map((r, i) => (
              <tr key={r.id} className={i % 2 === 0 ? styles.rowEven : styles.rowOdd}>
                <td className={styles.weekCell}>
                  <i className="ti ti-calendar" />
                  {r.weekLabel || formatWeekLabel(r.weekStart)}
                </td>
                <td className={styles.branchName}>{r.branchName || '—'}</td>
                <td className={styles.amount}>{php(r.weeklyRevenue)}</td>
                <td className={styles.amount}>{php(r.supplyExpense)}</td>
                <td className={`${styles.amount} ${(r.netProfit || 0) >= 0 ? styles.positive : styles.negative}`}>
                  {php(r.netProfit)}
                </td>
                <td>
                  <button className={styles.editBtn} onClick={() => onEdit(r)}>
                    <i className="ti ti-edit" /> Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function ReportsPage() {
  const { user }                           = useAuth();
  const [orders, setOrders]               = useState([]);
  const [transactions, setTransactions]   = useState([]);
  const [weeklyRecords, setWeeklyRecords] = useState([]);
  const [activeTab, setActiveTab]         = useState('Branch Performance');
  const [toast, setToast]                 = useState(null);
  const [editRow, setEditRow]             = useState(null);
  const [editRevenue, setEditRevenue]     = useState('');
  const [editExpense, setEditExpense]     = useState('');
  const [saving, setSaving]               = useState(false);
  const dismissToast                      = useCallback(() => setToast(null), []);

  useEffect(() => {
    const unsubOrders = onSnapshot(collection(db, 'orders'), snap => {
      setOrders(snap.docs.map(d => ({ _id: d.id, ...d.data() })));
    });
    const unsubTx = onSnapshot(collection(db, 'transactions'), snap => {
      setTransactions(snap.docs.map(d => ({ _id: d.id, ...d.data() })));
    });
    const q = query(collection(db, 'weeklyProfits'), orderBy('weekStart', 'desc'));
    const unsubWp = onSnapshot(q, snap => {
      setWeeklyRecords(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => { unsubOrders(); unsubTx(); unsubWp(); };
  }, []);

  const branchPerf = useMemo(() => {
    const map = {};
    for (const o of orders) {
      const name = o.branch ?? o.branchId ?? 'Unknown';
      if (!map[name]) map[name] = { branch: name, orders: 0, total: 0 };
      map[name].orders += 1;
      map[name].total  += typeof o.total === 'number' ? o.total : 0;
    }
    return Object.values(map)
      .sort((a, b) => b.total - a.total)
      .map(b => ({
        ...b,
        avgOrder: b.orders > 0 ? Math.round(b.total / b.orders) : 0,
        rating:   ratingFromOrders(b.orders),
      }));
  }, [orders]);

  const systemStats = useMemo(() => {
    const totalRevenue    = orders.reduce((s, o) => s + (typeof o.total === 'number' ? o.total : 0), 0);
    const pendingOrders   = orders.filter(o => o.status === 'Pending').length;
    const activeBranches  = new Set(orders.map(o => o.branchId)).size;
    const avgOrderValue   = orders.length > 0 ? Math.round(totalRevenue / orders.length) : 0;
    const txVerified      = transactions.filter(t => t.status === 'Verified').length;

    return [
      { label: 'Total Orders',          value: orders.length },
      { label: 'Total Revenue',         value: `₱${totalRevenue.toLocaleString()}` },
      { label: 'Active Branches',       value: activeBranches },
      { label: 'Pending Orders',        value: pendingOrders },
      { label: 'Avg Order Value',       value: `₱${avgOrderValue.toLocaleString()}` },
      { label: 'Transactions Verified', value: txVerified },
    ];
  }, [orders, transactions]);

  function openEdit(row) {
    setEditRow(row);
    setEditRevenue(String(row.weeklyRevenue ?? ''));
    setEditExpense(String(row.supplyExpense ?? ''));
  }

  function closeEdit() {
    setEditRow(null);
    setEditRevenue('');
    setEditExpense('');
  }

  async function handleSaveEdit() {
    const rev = parseFloat(editRevenue);
    const exp = parseFloat(editExpense);
    if (isNaN(rev) || isNaN(exp)) return;
    setSaving(true);
    try {
      await updateDoc(doc(db, 'weeklyProfits', editRow.id), {
        weeklyRevenue: rev,
        supplyExpense: exp,
        netProfit:     rev - exp,
        updatedAt:     serverTimestamp(),
      });
      await addAuditLog(user.uid, user.name, `Edited weekly profit record for ${editRow.branchName} (${editRow.weekLabel})`, 'Reports');
      setToast({ message: 'Record updated successfully.', type: 'success' });
      closeEdit();
    } catch (e) {
      console.error(e);
      setToast({ message: 'Failed to update record.', type: 'error' });
    } finally {
      setSaving(false);
    }
  }

  const editNet = (parseFloat(editRevenue) || 0) - (parseFloat(editExpense) || 0);

  return (
    <div className={styles.page}>
      <PageHeader title="Reports & Analytics" description="Branch performance, system analytics, and weekly profit records" />

      <div className={styles.tabRow}>
        {TABS.map(tab => (
          <button
            key={tab}
            className={`${styles.tab} ${activeTab === tab ? styles.tabActive : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab}
          </button>
        ))}
      </div>

      <div className={styles.tabContent}>
        {activeTab === 'Branch Performance' && <BranchPerfTab branchPerf={branchPerf} />}
        {activeTab === 'System Summary'     && <SystemSummaryTab stats={systemStats} />}
        {activeTab === 'Weekly Profits'     && (
          <WeeklyProfitsTab records={weeklyRecords} onEdit={openEdit} />
        )}
      </div>

      {/* Edit modal */}
      {editRow && (
        <div className={styles.overlay} onClick={e => { if (e.target === e.currentTarget) closeEdit(); }}>
          <div className={styles.editModal}>
            <h3 className={styles.editModalTitle}>Edit Weekly Profit Record</h3>
            <p className={styles.editModalSub}>
              <i className="ti ti-building-store" /> <strong>{editRow.branchName}</strong>
              {' — '}
              <i className="ti ti-calendar" /> {editRow.weekLabel || formatWeekLabel(editRow.weekStart)}
            </p>

            <div className={styles.editField}>
              <label className={styles.editLabel}>Weekly Revenue (₱)</label>
              <input
                type="number" min="0" step="0.01"
                className={styles.editInput}
                value={editRevenue}
                onChange={e => setEditRevenue(e.target.value)}
              />
            </div>

            <div className={styles.editField}>
              <label className={styles.editLabel}>Supply Order Expense (₱)</label>
              <input
                type="number" min="0" step="0.01"
                className={styles.editInput}
                value={editExpense}
                onChange={e => setEditExpense(e.target.value)}
              />
            </div>

            <div className={styles.editCalc}>
              <span className={styles.editCalcLabel}>Net Profit</span>
              <span className={`${styles.editCalcValue} ${editNet >= 0 ? styles.positive : styles.negative}`}>
                {php(editNet)}
              </span>
            </div>

            <div className={styles.editActions}>
              <button className={styles.cancelBtn} onClick={closeEdit} disabled={saving}>Cancel</button>
              <button className={styles.saveBtn} onClick={handleSaveEdit} disabled={saving}>
                <i className="ti ti-device-floppy" /> {saving ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <ToastNotification message={toast.message} type={toast.type} onDismiss={dismissToast} />
      )}
    </div>
  );
}
